/* assets/pl-snap-gallery.js - PL 2026-11-01
   Native scroll-snap engine for every product image gallery (main product page,
   quick-buy drawer and popover, featured-product sections).

   Focal builds each gallery with `new window.ThemeFlickity(element, options)`
   (FlickityCarousel._createFlickity in theme.js) after loading the "flickity"
   library. snippets/js-variables.liquid points that library at this file, so
   window.ThemeFlickity is this engine and flickity.min.js is never downloaded.
   Galleries are the only Flickity users in the theme (other sliders use native
   scrolling), checked 2026-11-01.

   Why: Flickity positions slides with JS on the main thread, needed adaptiveHeight
   measurements (which forced every slide image to download at start) and kept the
   phone gallery invisible until it booted. Here the browser scrolls and snaps the
   slides itself, so swipes stay smooth while other scripts run, the box is reserved
   by CSS from first paint, and slide images stay native loading="lazy".

   The object implements the part of the Flickity API that theme.js
   (FlickityCarousel, FlickityControls, ProductMedia), assets/accessibility.js and
   assets/pl-product-media.js use: selectedIndex, selectedElement, cells[].element,
   slides, options, element, viewport, select, selectCell, next, previous, resize,
   reloadCells, updateDraggable, destroy, on/off/once, ThemeFlickity.data(), and the
   ready / change / settle / staticClick events (also delivered through options.on,
   as Focal passes them). Options it does not reproduce (fade, adaptiveHeight,
   wrapAround) are ignored: every gallery shows one full-width slide at a time.
*/
(function () {
  'use strict';
  if (window.PLSnapGallery) return;

  var registry = typeof WeakMap === 'function' ? new WeakMap() : null;
  var reduceMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };

  function Snap(element, options) {
    this.element = element;
    this.viewport = element;
    this.options = Object.assign({}, options || {});
    this.isActive = true;
    this.__plSnap = true;
    this._handlers = {};
    this._targetIndex = -1;
    this._settledLeft = -9999;
    this._settleTimer = 0;
    this._raf = 0;
    this.cells = [];
    this.slides = [];
    this.selectedIndex = 0;
    this.selectedElement = null;
    this.selectedCell = null;

    if (registry) registry.set(element, this);
    element.classList.add('flickity-enabled', 'pl-snap');
    if (this.options.accessibility && !element.hasAttribute('tabindex')) {
      element.setAttribute('tabindex', '0');
      this._addedTabindex = true;
    }

    this.reloadCells();
    this._setSelected(this._initialIndex());
    this._jump(this.selectedIndex, true);
    this._settledLeft = element.scrollLeft;
    this._bind();

    // Flickity emits ready during construction; Focal's listener only re-emits it as a DOM event.
    this.emit('ready');
  }

  Snap.prototype._initialIndex = function () {
    var opt = this.options.initialIndex, el = this.element, target = null;
    if (typeof opt === 'number') return Math.max(0, Math.min(opt, this.cells.length - 1));
    if (opt) { try { target = el.querySelector(opt); } catch (e) { target = null; } }
    if (!target) target = el.querySelector('.product__media-item.is-selected:not(.is-filtered)');
    var i = this._indexOf(target);
    return i < 0 ? 0 : i;
  };

  Snap.prototype.reloadCells = function () {
    var sel = this.options.cellSelector || '*', el = this.element;
    var list = Array.prototype.filter.call(el.children, function (child) {
      try { return child.matches(sel); } catch (e) { return true; }
    });
    this.cells = list.map(function (node) { return { element: node, target: 0 }; });
    // One cell per slide, as with Flickity's default groupCells:false.
    this.slides = this.cells.map(function (cell) { return { cells: [cell], getCellElements: function () { return [cell.element]; } }; });
    if (this.selectedIndex >= this.cells.length) this.selectedIndex = Math.max(0, this.cells.length - 1);
  };

  Snap.prototype._indexOf = function (node) {
    if (!node) return -1;
    for (var i = 0; i < this.cells.length; i++) {
      var cellEl = this.cells[i].element;
      if (cellEl === node || cellEl.contains(node)) return i;
    }
    return -1;
  };

  Snap.prototype._setSelected = function (index) {
    if (!this.cells.length) { this.selectedElement = null; this.selectedCell = null; return; }
    this.selectedIndex = index;
    this.selectedCell = this.cells[index];
    this.selectedElement = this.selectedCell.element;
    var selected = this.selectedElement;
    this.cells.forEach(function (cell) {
      var on = cell.element === selected;
      cell.element.classList.toggle('is-selected', on);
      // Same as Flickity: off-screen slides are hidden from assistive tech;
      // pl-product-media.js removes their controls from the tab order.
      if (on) cell.element.removeAttribute('aria-hidden');
      else cell.element.setAttribute('aria-hidden', 'true');
    });
  };

  Snap.prototype._offsetOf = function (index) {
    var cell = this.cells[index];
    if (!cell) return 0;
    var el = this.element;
    return el.scrollLeft + (cell.element.getBoundingClientRect().left - el.getBoundingClientRect().left);
  };

  Snap.prototype._jump = function (index, instant) {
    if (!this.cells.length) return;
    var left = this._offsetOf(index);
    var behavior = (instant || reduceMotion.matches) ? 'auto' : 'smooth';
    // Only a smooth scroll has a pending target; an instant jump replaces any earlier one.
    this._targetIndex = (behavior === 'smooth' && Math.abs(left - this.element.scrollLeft) > 1) ? index : -1;
    try { this.element.scrollTo({ left: left, behavior: behavior }); }
    catch (e) { this.element.scrollLeft = left; }
  };

  Snap.prototype._nearestIndex = function () {
    var el = this.element, center = el.getBoundingClientRect().left + el.clientWidth / 2;
    var best = this.selectedIndex, bestDist = Infinity;
    for (var i = 0; i < this.cells.length; i++) {
      var r = this.cells[i].element.getBoundingClientRect();
      var d = Math.abs(r.left + r.width / 2 - center);
      if (d < bestDist) { bestDist = d; best = i; }
    }
    return best;
  };

  Snap.prototype._bind = function () {
    var self = this, el = this.element;

    this._onScroll = function () {
      if (self._raf) return;
      self._raf = requestAnimationFrame(function () {
        self._raf = 0;
        // While a programmatic smooth scroll runs, slides passed on the way are not selections.
        if (Math.abs(el.scrollLeft - self._settledLeft) > 2) self._needSettle = true;
        if (self._targetIndex < 0) {
          var i = self._nearestIndex();
          if (i !== self.selectedIndex) self._change(i);
        }
        clearTimeout(self._settleTimer);
        self._settleTimer = setTimeout(function () { self._settle(); }, 140);
      });
    };
    this._onScrollEnd = function () { clearTimeout(self._settleTimer); self._settle(); };

    // Mouse drag (touch and trackpads scroll natively).
    var drag = null;
    this._onPointerDown = function (e) {
      self._down = { x: e.clientX, y: e.clientY };
      self._targetIndex = -1; // the user takes over from any programmatic scroll
      if (e.pointerType !== 'mouse' || e.button !== 0 || self.cells.length < 2) return;
      drag = { x: e.clientX, left: el.scrollLeft, moved: false, id: e.pointerId };
    };
    this._onPointerMove = function (e) {
      if (!drag || e.pointerId !== drag.id) return;
      var dx = e.clientX - drag.x;
      if (!drag.moved && Math.abs(dx) < (self.options.dragThreshold || 10)) return;
      if (!drag.moved) { drag.moved = true; el.classList.add('is-dragging'); try { el.setPointerCapture(e.pointerId); } catch (err) {} }
      el.scrollLeft = drag.left - dx;
    };
    this._onPointerUp = function (e) {
      if (!drag || e.pointerId !== drag.id) return;
      var d = drag; drag = null;
      if (!d.moved) return;
      self._suppressClick = true;
      el.classList.remove('is-dragging');
      var dx = e.clientX - d.x, i = self._nearestIndex();
      // A short flick still moves one slide in the drag direction.
      if (i === self.selectedIndex && Math.abs(dx) > 40) i = self.selectedIndex + (dx < 0 ? 1 : -1);
      self.select(i);
    };
    this._onClick = function (e) {
      if (self._suppressClick) { self._suppressClick = false; e.preventDefault(); e.stopPropagation(); }
    };
    // Bubble phase, so handlers inside the slide run first: a click they already handled
    // (desktop image zoom calls preventDefault) is not also a slide change.
    this._onStaticClick = function (e) {
      if (e.defaultPrevented) return;
      var down = self._down;
      if (down && (Math.abs(e.clientX - down.x) > 10 || Math.abs(e.clientY - down.y) > 10)) return;
      var i = self._indexOf(e.target);
      self.emit('staticClick', e, e, i >= 0 ? self.cells[i].element : null, i);
    };
    this._onKeyDown = function (e) {
      if (!self.options.accessibility || document.activeElement !== el) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); self.previous(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); self.next(); }
    };
    this._onWheel = function () { self._targetIndex = -1; };
    this._onResize = function () { self.resize(); };

    el.addEventListener('scroll', this._onScroll, { passive: true });
    el.addEventListener('scrollend', this._onScrollEnd);
    el.addEventListener('pointerdown', this._onPointerDown);
    el.addEventListener('pointermove', this._onPointerMove);
    el.addEventListener('pointerup', this._onPointerUp);
    el.addEventListener('pointercancel', this._onPointerUp);
    el.addEventListener('click', this._onClick, true);
    el.addEventListener('click', this._onStaticClick);
    el.addEventListener('keydown', this._onKeyDown);
    el.addEventListener('wheel', this._onWheel, { passive: true });
    if ('ResizeObserver' in window) {
      this._ro = new ResizeObserver(this._onResize);
      this._ro.observe(el);
    } else {
      window.addEventListener('resize', this._onResize);
    }
  };

  Snap.prototype._change = function (index) {
    this._needSettle = true;
    this._setSelected(index);
    this.emit('select', index);
    this.emit('change', index);
  };

  Snap.prototype._settle = function () {
    var target = this._targetIndex;
    this._targetIndex = -1;
    var i = this._nearestIndex();
    if (target >= 0 && i !== target) { this._jump(target, true); i = target; }
    if (i !== this.selectedIndex) this._change(i);
    // One settle per move: scroll, scrollend and instant selects can all land here.
    if (!this._needSettle) return;
    this._needSettle = false;
    this._settledLeft = this.element.scrollLeft;
    this.emit('settle', this.selectedIndex);
  };

  // ---- Flickity API ----------------------------------------------------
  Snap.prototype.select = function (index, isWrap, isInstant) {
    var n = this.cells.length;
    if (!n) return;
    index = parseInt(index, 10) || 0;
    if (isWrap || this.options.wrapAround) index = ((index % n) + n) % n;
    else index = Math.max(0, Math.min(index, n - 1));
    var changed = index !== this.selectedIndex;
    if (changed) this._change(index);
    this._jump(index, isInstant);
    this._needSettle = true;
    if (isInstant || reduceMotion.matches) {
      var self = this;
      requestAnimationFrame(function () { self._settle(); });
    }
  };

  Snap.prototype.selectCell = function (value, isWrap, isInstant) {
    var index = value;
    if (typeof value === 'string') {
      var node = null;
      try { node = this.element.querySelector(value); } catch (e) { node = null; }
      index = this._indexOf(node);
    } else if (value && value.nodeType === 1) {
      index = this._indexOf(value);
    }
    if (typeof index !== 'number' || index < 0) return;
    this.select(index, isWrap, isInstant);
  };

  Snap.prototype.next = function (isWrap, isInstant) { this.select(this.selectedIndex + 1, isWrap, isInstant); };
  Snap.prototype.previous = function (isWrap, isInstant) { this.select(this.selectedIndex - 1, isWrap, isInstant); };

  // Layout changed (viewport resize, cells shown/hidden): keep the selected slide in place.
  Snap.prototype.resize = function () {
    if (!this.isActive) return;
    this._jump(this.selectedIndex, true);
  };
  Snap.prototype.reposition = Snap.prototype.resize;
  Snap.prototype.updateDraggable = function () {};
  Snap.prototype.getCellElements = function () { return this.cells.map(function (c) { return c.element; }); };

  Snap.prototype.destroy = function () {
    if (!this.isActive) return;
    this.isActive = false;
    var el = this.element;
    if (registry) registry.delete(el);
    el.removeEventListener('scroll', this._onScroll);
    el.removeEventListener('scrollend', this._onScrollEnd);
    el.removeEventListener('wheel', this._onWheel);
    el.removeEventListener('pointerdown', this._onPointerDown);
    el.removeEventListener('pointermove', this._onPointerMove);
    el.removeEventListener('pointerup', this._onPointerUp);
    el.removeEventListener('pointercancel', this._onPointerUp);
    el.removeEventListener('click', this._onClick, true);
    el.removeEventListener('click', this._onStaticClick);
    el.removeEventListener('keydown', this._onKeyDown);
    if (this._ro) this._ro.disconnect(); else window.removeEventListener('resize', this._onResize);
    clearTimeout(this._settleTimer);
    if (this._raf) cancelAnimationFrame(this._raf);
    el.classList.remove('flickity-enabled', 'pl-snap', 'is-dragging');
    if (this._addedTabindex) el.removeAttribute('tabindex');
    this.cells.forEach(function (cell) { cell.element.removeAttribute('aria-hidden'); });
    this.emit('destroy');
    this._handlers = {};
  };

  // ---- events --------------------------------------------------------
  Snap.prototype.on = function (name, fn) { (this._handlers[name] = this._handlers[name] || []).push(fn); return this; };
  Snap.prototype.off = function (name, fn) {
    var list = this._handlers[name];
    if (list) this._handlers[name] = list.filter(function (h) { return h !== fn && h.__orig !== fn; });
    return this;
  };
  Snap.prototype.once = function (name, fn) {
    var self = this, wrapped = function () { self.off(name, wrapped); return fn.apply(this, arguments); };
    wrapped.__orig = fn;
    return this.on(name, wrapped);
  };
  Snap.prototype.emit = function (name) {
    var args = Array.prototype.slice.call(arguments, 1), self = this;
    var opt = this.options.on && this.options.on[name];
    if (typeof opt === 'function') { try { opt.apply(self, args); } catch (e) { console.warn('[pl-snap]', name, e); } }
    (this._handlers[name] || []).slice().forEach(function (fn) {
      try { fn.apply(self, args); } catch (e) { console.warn('[pl-snap]', name, e); }
    });
  };
  Snap.prototype.dispatchEvent = function (name, event, args) { this.emit.apply(this, [name].concat(args || [])); };

  window.PLSnapGallery = {
    create: function (el, options) { return new Snap(el, options); },
    // Same lookup as Flickity.data(element or selector), used by assets/accessibility.js.
    data: function (el) {
      if (typeof el === 'string') el = document.querySelector(el);
      return (registry && el) ? registry.get(el) : undefined;
    }
  };

  // Stands in for Flickity: Focal calls `new window.ThemeFlickity(element, options)`.
  function ThemeFlickity(element, options) { return new Snap(element, options); }
  ThemeFlickity.data = window.PLSnapGallery.data;
  ThemeFlickity.prototype = Snap.prototype;
  window.ThemeFlickity = ThemeFlickity;
})();
